/**
 * Logos e imágenes del sistema (sección *Logos e imágenes* de Empresa).
 *
 * Cada imagen se sube por separado, como en el panel anterior: al elegir el
 * archivo se envía en binario y la API devuelve la imagen ya resuelta (con su
 * nueva marca de tiempo, que se usa para refrescar la vista previa).
 */

import { Image as IconoImagen, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

import type { CampoImagenEmpresa, ImagenEmpresa } from '../empresa.types';
import { urlImagenEmpresa } from '../empresa.api';

/** Formatos que acepta el panel anterior (`Empresa::subir_actualizar_imagen`). */
const ACEPTADOS = 'image/png,image/jpeg,image/jpg,image/x-icon,image/vnd.microsoft.icon';
const MAX_BYTES = 4 * 1024 * 1024;

interface Props {
  imagenes: Record<CampoImagenEmpresa, ImagenEmpresa>;
  /** Campo que se está subiendo ahora mismo (para el spinner del botón). */
  subiendoCampo: CampoImagenEmpresa | null;
  error: string | null;
  /** La página confirma antes de subir (el legacy pedía confirmación también). */
  onElegir: (campo: CampoImagenEmpresa, archivo: File) => void;
}

/** Una imagen con su vista previa y el botón para reemplazarla. */
function TarjetaImagen({
  imagen,
  subiendo,
  onSubir,
}: {
  imagen: ImagenEmpresa;
  subiendo: boolean;
  onSubir: (archivo: File) => void;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const elegir = (archivo: File | undefined) => {
    if (!archivo) return;

    if (archivo.size > MAX_BYTES) {
      setAviso(`La imagen pesa más de ${MAX_BYTES / (1024 * 1024)} MB.`);
      return;
    }

    setAviso(null);
    onSubir(archivo);
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm font-semibold text-ink-900">{imagen.etiqueta}</p>

        <div className="flex h-28 items-center justify-center rounded-control border border-ink-100 bg-ink-50/60 p-2">
          {imagen.ruta ? (
            <img
              key={imagen.version ?? 'defecto'}
              src={urlImagenEmpresa(imagen.campo, imagen.version)}
              alt={imagen.etiqueta}
              className="max-h-24 max-w-full object-contain"
            />
          ) : (
            <span className="flex flex-col items-center gap-1 text-ink-400">
              <IconoImagen className="size-6" aria-hidden="true" />
              <span className="text-[11px]">Sin imagen cargada</span>
            </span>
          )}
        </div>

        <p className="truncate text-[11px] text-ink-500" title={imagen.ruta ?? undefined}>
          {imagen.por_defecto
            ? imagen.ruta
              ? 'Imagen por defecto del sistema'
              : 'Sin imagen cargada'
            : imagen.ruta}
        </p>

        {aviso ? <Alert variant="warning">{aviso}</Alert> : null}

        <input
          ref={entrada}
          type="file"
          accept={ACEPTADOS}
          className="hidden"
          onChange={(evento) => {
            elegir(evento.target.files?.[0]);
            evento.target.value = ''; // permite volver a elegir el mismo archivo
          }}
        />

        <Button
          variant="outline"
          size="sm"
          fullWidth
          className="mt-auto"
          leftIcon={<Upload className="size-4" aria-hidden="true" />}
          loading={subiendo}
          onClick={() => entrada.current?.click()}
        >
          Cambiar imagen
        </Button>
      </CardContent>
    </Card>
  );
}

/** Rejilla con las seis imágenes del sistema. */
export function EmpresaLogos({ imagenes, subiendoCampo, error, onElegir }: Props) {
  const lista = Object.values(imagenes);

  return (
    <div className="flex flex-col gap-4">
      {error ? <Alert variant="error" title="No se pudo subir la imagen">{error}</Alert> : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {lista.map((imagen) => (
          <TarjetaImagen
            key={imagen.campo}
            imagen={imagen}
            subiendo={subiendoCampo === imagen.campo}
            onSubir={(archivo) => onElegir(imagen.campo, archivo)}
          />
        ))}
      </div>

      <Alert variant="info" title="Sobre los formatos">
        <span className="flex items-start gap-2">
          <IconoImagen className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            Se admiten PNG, JPG e ICO de hasta 4 MB. Si la ruta guardada ya no existe en el
            servidor, la pantalla muestra automáticamente la imagen por defecto del sistema.
          </span>
        </span>
      </Alert>
    </div>
  );
}
